/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { BundleType } from '../../resources/nls/bundle';
import type { GanttDatatipContext, Gantt as GanttProps, GanttRow, GanttTask } from '../Gantt.types';
import { styles } from '../../hooks/PRIVATE_useChartDatatip/themes/ChartDatatipStyles.css';
import { classNames } from '../../utils/UNSAFE_classNames';
import { getNormalizedStartEnd } from './dataLayoutUtils';

/**
 * Gets the default datatip renderer.
 * @param options Relevant information.
 * @returns The default datatip renderer.
 */
const getDefaultDatatipRenderer = <
  K1,
  K2,
  D1 extends GanttRow<K1, D1, D2>,
  D2 extends GanttTask<K2>
>(options: {
  translations: BundleType;
  dateFormatter: (date: string) => string;
  isRTL: boolean;
}): NonNullable<GanttProps<K2, D1, D2>['datatip']> => {
  const { translations, dateFormatter, isRTL } = options;
  const defaultDatatipRenderer = ({ data, rowData }: GanttDatatipContext<D1, D2>) => {
    if (data.accessibleLabel !== undefined) {
      return { content: data.accessibleLabel };
    }

    // TODO: Use shared tabular datatip component when that's available
    const datatipLabelStyle = classNames([
      styles.datatipLabel,
      isRTL ? styles.leftAlign : styles.rightAlign
    ]);
    const datatipValueStyle = classNames([
      styles.datatipValue,
      isRTL ? styles.rightAlign : styles.leftAlign
    ]);

    const { start, end } = getNormalizedStartEnd(data);
    const isMilestone = start === end;

    const content = (
      <table class={styles.datatipTable}>
        {rowData.label && (
          <tr>
            <td class={datatipLabelStyle}>{translations.gantt_labelRow()}</td>
            <td class={datatipValueStyle}>{rowData.label}</td>
          </tr>
        )}
        {!isMilestone && (
          <>
            <tr>
              <td class={datatipLabelStyle}>{translations.gantt_labelStart()}</td>
              <td class={datatipValueStyle}>{dateFormatter(start)}</td>
            </tr>
            <tr>
              <td class={datatipLabelStyle}>{translations.gantt_labelEnd()}</td>
              <td class={datatipValueStyle}>{dateFormatter(end)}</td>
            </tr>
          </>
        )}
        {isMilestone && (
          <tr>
            <td class={datatipLabelStyle}>{translations.gantt_labelDate()}</td>
            <td class={datatipValueStyle}>{dateFormatter(start)}</td>
          </tr>
        )}
        {data.label && (
          <tr>
            <td class={datatipLabelStyle}>{translations.gantt_labelLabel()}</td>
            <td class={datatipValueStyle}>{data.label}</td>
          </tr>
        )}
      </table>
    );
    return { content };
  };
  return defaultDatatipRenderer;
};

/**
 * Gets the datatip renderer context.
 * @param rows The rows data.
 * @param layoutObj The task layout object.
 * @returns
 */
const getRenderContext = <K1, K2, D1 extends GanttRow<K1, D1, D2>, D2 extends GanttTask<K2>>(
  rows: GanttProps<K2, D1, D2>['rows'],
  layoutObj: { index: number; rowIndex: number }
): GanttDatatipContext<D1, D2> => {
  const { index, rowIndex } = layoutObj;
  return {
    data: rows[rowIndex].tasks[index],
    rowData: rows[rowIndex]
  };
};

export { getDefaultDatatipRenderer, getRenderContext };
